// Stable color per host "kind" so cards + filter dots are consistent.
export interface HostPalette {
  dot: string // small solid swatch
  chip: string // card / chip background + text + ring
}

const NEUTRAL: HostPalette = {
  dot: 'bg-slate-400',
  chip: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:ring-slate-500/30',
}

const PALETTES: HostPalette[] = [
  { dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-500/30' },
  { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30' },
  { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/30' },
  { dot: 'bg-sky-500', chip: 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/15 dark:text-sky-300 dark:ring-sky-500/30' },
  { dot: 'bg-violet-500', chip: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/15 dark:text-violet-300 dark:ring-violet-500/30' },
  { dot: 'bg-fuchsia-500', chip: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-500/15 dark:text-fuchsia-300 dark:ring-fuchsia-500/30' },
  { dot: 'bg-teal-500', chip: 'bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-500/15 dark:text-teal-300 dark:ring-teal-500/30' },
  { dot: 'bg-orange-500', chip: 'bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/15 dark:text-orange-300 dark:ring-orange-500/30' },
  { dot: 'bg-brick-500', chip: 'bg-brick-50 text-brick-700 ring-brick-200 dark:bg-brick-500/15 dark:text-brick-300 dark:ring-brick-500/30' },
  { dot: 'bg-lime-500', chip: 'bg-lime-50 text-lime-800 ring-lime-200 dark:bg-lime-500/15 dark:text-lime-300 dark:ring-lime-500/30' },
  { dot: 'bg-cyan-500', chip: 'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/15 dark:text-cyan-300 dark:ring-cyan-500/30' },
  { dot: 'bg-pink-500', chip: 'bg-pink-50 text-pink-700 ring-pink-200 dark:bg-pink-500/15 dark:text-pink-300 dark:ring-pink-500/30' },
]

// Categories pinned to a palette instead of taking whatever the hash gives them.
// Values index into PALETTES above.
//
// The hash spreads 15 categories over 12 palettes, so collisions are arithmetic
// rather than bad luck - five categories already share lime. That is survivable
// while the colliding pair is rare, and it is what the three categories added by
// the 7/29 taxonomy would have walked into: Tours & History hashes onto lime,
// making a sixth, and Food & Drink onto brick beside Karaoke. Pinning them takes
// three of the palettes the retired categories freed up, and changes no existing
// category's colour.
//
// This is a patch on a hash, not a colour system. Assigning all fifteen on
// purpose is a design pass with the brand kit open, not something to do while
// shipping a data migration.
const PINNED: Record<string, number> = {
  'Food & Drink': 2, // emerald
  'Tours & History': 10, // cyan, freed by Class/Workshop retiring
  'Giving & Volunteering': 4, // violet, where the hash puts it anyway - pinned so it stays
  // Support and Animals (added 2026-08-19) both hash onto a palette already
  // taken - Support lands on Dance's orange, Animals on Fitness & Wellness's
  // pink. Pinned to the two palettes nothing else uses instead of adding a
  // sixth and seventh collision.
  Support: 6, // teal
  Animals: 5, // fuchsia
  // Vendors Wanted hashes onto brick(8), which Karaoke already sits on AND is
  // the site's own brand/CTA color - a filter chip in the same brick as every
  // "Subscribe" and "Approve" button reads as an accent, not a category. amber
  // was free once Support took teal the same day (8/19).
  'Vendors Wanted': 1, // amber
}

function hash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return h
}

export function hostColor(host: string | null): HostPalette {
  if (!host) return NEUTRAL
  const pinned = PINNED[host]
  if (pinned !== undefined) return PALETTES[pinned]
  return PALETTES[hash(host) % PALETTES.length]
}
